import { useMemo } from "react"
import { Link } from "react-router-dom"
import { FOREIGN_BRAND_PRICE_GROUPS } from "../api/config"
import { describeError } from "../api/http"
import { membershipOf, useGroups, useGroupsByProduct, usePriceGroups } from "../api/queries"
import { useAccess } from "../auth/rights"
import GroupThumb from "../components/GroupThumb"
import { Note } from "../components/Note"
import Pager from "../components/Pager"
import { useDebounced } from "../hooks/useDebounced"
import { usePaged } from "../hooks/usePaged"
import { useStoredEnum, useStoredText } from "../hooks/useStored"
import { useLanguageId } from "../lib/language"
import { StorageKey } from "../lib/storage"
import type { GroupProductHit } from "../api/types"

const PAGE_SIZE = 50

type Scope = "own" | "foreign" | "all"

const SCOPES: { value: Scope; label: string }[] = [
	{ value: "own", label: "Свои" },
	{ value: "foreign", label: "РФ" },
	{ value: "all", label: "Все" },
]

/** Подфильтр по марке — только внутри «РФ», остальные ~530 РФ-групп вне двух
 *  основных марок остаются в «Все» (см. FOREIGN_BRAND_PRICE_GROUPS). */
type Brand = "all" | "avtodelo" | "beril"

const BRANDS: { value: Brand; label: string; priceGroupId: number | null }[] = [
	{ value: "all", label: "Все", priceGroupId: null },
	{ value: "avtodelo", label: "Автодело", priceGroupId: FOREIGN_BRAND_PRICE_GROUPS.avtodelo },
	{ value: "beril", label: "Берил", priceGroupId: FOREIGN_BRAND_PRICE_GROUPS.beril },
]

/**
 * Список групп. Приходит одним ответом целиком (3 582 строки, 665 КБ, 24 мс на
 * стенде), поэтому отбор, поиск и счётчики считаются здесь, а не запросом.
 *
 * Тип, марка и поиск живут в localStorage (`useStoredEnum`/`useStoredText`), а
 * не в `useState`: переход в карточку группы размонтирует этот экран, и без
 * хранилища возврат по «← к списку групп» всегда открывал бы «Свои» с пустым
 * поиском — не то место, откуда ушли.
 *
 * Единственное, что считается не здесь, — поиск по товарам внутри групп
 * (`useGroupsByProduct`): товаров экран не грузит вовсе, их по артикулу и
 * названию ищет рижский BFF в ERP. Найденные группы добавляются к тем, что
 * совпали кодом или названием сами, и проходят те же фильтры «Свои/РФ» и марки.
 * Исключение — группа, чей код набран целиком: она первой и на любой вкладке,
 * даже «ничейная» (только что созданная, ещё без товаров).
 */
export default function GroupsScreen() {
	const languageId = useLanguageId()
	const groups = useGroups(languageId)
	const priceGroups = usePriceGroups()
	const { isManager } = useAccess()

	const [scope, setScope] = useStoredEnum<Scope>(
		StorageKey.groupsScope,
		"own",
		SCOPES.map(item => item.value)
	)
	const [brand, setBrand] = useStoredEnum<Brand>(
		StorageKey.groupsBrand,
		"all",
		BRANDS.map(item => item.value)
	)
	const [search, setSearch] = useStoredText(StorageKey.groupsSearch)
	const productHits = useGroupsByProduct(useDebounced(search), languageId)

	/** Группа → первый совпавший товар: по нему подписывается строка списка.
	 *  Пустой поиск обнуляет карту, не дожидаясь ответа: `placeholderData`
	 *  держит прошлую выдачу, и без этого подписи «товар …» остались бы висеть
	 *  на строках после очистки поля. */
	const hitByGroup = useMemo(() => {
		const found = new Map<string, GroupProductHit>()
		if (search.trim().length < 2) return found
		for (const hit of productHits.data ?? []) {
			if (!found.has(hit.groupId)) found.set(hit.groupId, hit)
		}
		return found
	}, [productHits.data, search])

	const priceGroupName = useMemo(() => {
		const names = new Map<number, string>()
		for (const item of priceGroups.data ?? []) names.set(item.id, item.name)
		return names
	}, [priceGroups.data])

	// «Ничейные» (PriceGroupID пуст) в списке скрыты — решение №6; найти можно по точному коду
	const visible = useMemo(
		() =>
			(groups.data ?? []).filter(
				group => membershipOf(group.priceGroupId) !== "none"
			),
		[groups.data]
	)

	// Марка фильтрует только внутри «РФ» — на «Свои» и «Все» не действует, даже
	// если осталась выбранной с прошлого раза.
	const brandPriceGroupId =
		scope === "foreign"
			? (BRANDS.find(item => item.value === brand)?.priceGroupId ?? null)
			: null

	const needle = search.trim().toLowerCase()

	/** Совпавшие с поиском — до вкладок и марки: по ним же считаются счётчики
	 *  на вкладках, чтобы при поиске было видно, в какой вкладке нашлось. */
	const matched = useMemo(() => {
		if (!needle) return visible
		return visible.filter(
			group =>
				group.name.toLowerCase().includes(needle) ||
				group.id.toLowerCase().includes(needle) ||
				hitByGroup.has(group.id)
		)
	}, [visible, needle, hitByGroup])

	/** Группа, чей код набран целиком. Код у группы один, поэтому такую группу
	 *  показываем первой и на любой вкладке: иначе код РФ-группы на «Свои»
	 *  (вкладка по умолчанию) давал «Ничего не найдено», будто по коду не ищет.
	 *  Ищется по всему набору, включая «ничейные»: ничейная сегодня — это
	 *  только что созданная группа без товаров (прайс-группа приходит с первым
	 *  товаром), и без этого после ухода из карточки до неё было не добраться. */
	const exact = useMemo(
		() =>
			needle
				? (groups.data ?? []).find(group => group.id.toLowerCase() === needle)
				: undefined,
		[groups.data, needle]
	)

	const filtered = useMemo(() => {
		const inTab = matched.filter(group => {
			if (group === exact) return false
			if (scope !== "all" && membershipOf(group.priceGroupId) !== scope) {
				return false
			}
			if (brandPriceGroupId !== null && group.priceGroupId !== brandPriceGroupId) {
				return false
			}
			return true
		})
		return exact ? [exact, ...inTab] : inTab
	}, [matched, exact, scope, brandPriceGroupId])

	const paged = usePaged(filtered, PAGE_SIZE)

	const counts = useMemo(() => {
		let own = 0
		let avtodelo = 0
		let beril = 0
		for (const group of matched) {
			const membership = membershipOf(group.priceGroupId)
			if (membership === "own") own++
			if (membership === "foreign") {
				if (group.priceGroupId === FOREIGN_BRAND_PRICE_GROUPS.avtodelo) avtodelo++
				else if (group.priceGroupId === FOREIGN_BRAND_PRICE_GROUPS.beril) beril++
			}
		}
		const foreign = matched.length - own
		return { own, foreign, all: matched.length, avtodelo, beril }
	}, [matched])

	const brandCount: Record<Brand, number> = {
		all: counts.foreign,
		avtodelo: counts.avtodelo,
		beril: counts.beril,
	}

	return (
		<>
			<div className="toolbar">
				<div className="segmented">
					{SCOPES.map(item => (
						<button
							key={item.value}
							aria-pressed={scope === item.value}
							onClick={() => {
								setScope(item.value)
								paged.reset()
							}}
						>
							{item.label} · {counts[item.value]}
						</button>
					))}
				</div>

				{scope === "foreign" && (
					<div className="groups-field">
						<span>Марка</span>
						<div className="segmented">
							{BRANDS.map(item => (
								<button
									key={item.value}
									aria-pressed={brand === item.value}
									onClick={() => {
										setBrand(item.value)
										paged.reset()
									}}
								>
									{item.label} · {brandCount[item.value]}
								</button>
							))}
						</div>
					</div>
				)}

				<div className="search">
					<span className="search__icon">⌕</span>
					<input
						className="input"
						placeholder="Поиск по названию, коду и товарам внутри"
						value={search}
						onChange={event => {
							setSearch(event.target.value)
							paged.reset()
						}}
					/>
				</div>

				<div className="counter">
					{groups.isLoading
						? "загрузка…"
						: `найдено ${filtered.length.toLocaleString("ru")}`}
				</div>

				{isManager && (
					<Link to="/groups/new" className="btn btn--primary">
						+ Новая группа
					</Link>
				)}
			</div>

			<div className="table-wrap">
				<div className="grid-head">
					<div>Фото</div>
					<div>Код</div>
					<div>Название</div>
					<div>Прайс-группа</div>
					<div>Проверено</div>
					<div>Тип</div>
				</div>

				{groups.isLoading && <LoadingRows />}

				{groups.isError && (
					<div className="state">
						<Note tone="error">
							Не удалось загрузить группы: {describeError(groups.error)}
						</Note>
					</div>
				)}

				{!groups.isLoading && paged.rows.length === 0 && (
					<div className="state">Ничего не найдено</div>
				)}

				{paged.rows.map(group => {
					const membership = membershipOf(group.priceGroupId)
					const own = membership === "own"
					return (
						<Link
							key={group.id}
							to={`/groups/${group.id}`}
							className="grid-row"
						>
							<GroupThumb groupId={group.id} />
							<div className="mono muted">{group.id}</div>
							<div className="cell-name" title={group.name}>
								{group.name || <span className="muted">без названия</span>}
								{hitByGroup.get(group.id) && (
									<span className="cell-name__hit">
										товар {hitByGroup.get(group.id)!.productId}
										{hitByGroup.get(group.id)!.productName
											? ` · ${hitByGroup.get(group.id)!.productName}`
											: ""}
									</span>
								)}
							</div>
							<div className="owner">
								{membership === "none" ? (
									<>
										<span
											className="tag tag--muted"
											title="Прайс-группа появится с первым товаром. Пока её нет, группа находится только по точному коду"
										>
											без прайс-группы
										</span>
										<span className="owner__note">нет товаров</span>
									</>
								) : (
									<>
										<span
											className={"tag " + (own ? "tag--own" : "tag--foreign")}
											title={
												own
													? "Группа Риги — полное редактирование"
													: "Группа РФ — доступен только перевод"
											}
										>
											{own
												? "Рига"
												: (priceGroupName.get(group.priceGroupId ?? -1) ??
													`#${group.priceGroupId}`)}
										</span>
										{!own && <span className="owner__note">только перевод</span>}
									</>
								)}
							</div>
							<div className="checks">
								<span title="Фото проверены">
									<i
										className={
											"dot " + (group.isImageChecked ? "dot--ok" : "dot--no")
										}
									/>
									фото
								</span>
								<span title="Описание проверено">
									<i
										className={
											"dot " +
											(group.isDescriptionChecked ? "dot--ok" : "dot--no")
										}
									/>
									опис.
								</span>
							</div>
							<div>
								{group.isToolset ? (
									<span className="tag tag--muted">набор</span>
								) : (
									<span className="muted">—</span>
								)}
							</div>
						</Link>
					)
				})}

				<Pager
					page={paged.page}
					pageCount={paged.pageCount}
					onGo={paged.goTo}
				/>
			</div>
		</>
	)
}

function LoadingRows() {
	return (
		<>
			{Array.from({ length: 8 }, (_, index) => (
				<div className="grid-row grid-row--skeleton" key={index}>
					<div className="thumb skeleton" />
					<div className="skeleton skeleton--line" />
					<div className="skeleton skeleton--line" />
					<div className="skeleton skeleton--line" />
					<div className="skeleton skeleton--line" />
					<div className="skeleton skeleton--line" />
				</div>
			))}
		</>
	)
}
